import type {TableRowData} from 'shells/automated-email-table/automated-email-table';

import type {GetEmailAutomationsNeedingAttention_emailAutomationStatusesNeedsAttention_edges} from 'nutshell-graphql-types';

/**
 * This helper function converts the graphql response of emailAutomationStatus edges
 * into the structure required by our email automation tables.
 */
export function convertEdgesToTableData(
    edges: GetEmailAutomationsNeedingAttention_emailAutomationStatusesNeedsAttention_edges[]
): TableRowData[] {
    return edges.map((edge) => {
        const lead = edge.node.lead;
        const contact = edge.node.lead.contacts.primaryEdge;
        const emailSequenceTemplate = edge.node.emailAutomation.emailList;
        const emailSequence = edge.node.emailSequence;
        const firstName = edge.node.firstName;
        const lastName = edge.node.lastName;
        const companyName = edge.node.companyName;
        const emailAddress = edge.node.emailAddress;

        return {
            emailAutomationStatus: {
                id: edge.node.id,
                status: edge.node.status,
                isPaused: edge.node.isPaused,
                sender: edge.node.sender
                    ? {
                          id: edge.node.sender.id,
                      }
                    : null,
            },
            lead: {
                leadName: lead.name,
                leadNumber: lead.number,
                leadStatus: lead.status,
                priority: lead.priority,
                htmlUrl: lead.htmlUrl,
                avatarUrl: lead.avatarUrl,
                primaryContact: contact
                    ? {
                          id: contact.node.id,
                          name: contact.node.name,
                          htmlUrl: contact.node.htmlUrl,
                          type: 'contacts',
                      }
                    : null,
            },
            templateFields: {
                firstName: {
                    materializedValue: firstName.value,
                    required: firstName.required,
                },
                lastName: {
                    materializedValue: lastName.value,
                    required: lastName.required,
                },
                companyName: {
                    materializedValue: companyName.value,
                    required: companyName.required,
                },
                emailAddress: {
                    // Temporarily nullable while we release API changes.
                    materializedValue: emailAddress ? emailAddress.value : null,
                    required: emailAddress ? emailAddress.required : true,
                },
            },
            emailSequenceTemplate: emailSequenceTemplate
                ? {
                      id: emailSequenceTemplate.id,
                      sequenceName: emailSequenceTemplate.name || 'Unnamed emails',
                      messageCount: Array.isArray(
                          // @ts-expect-error
                          emailSequenceTemplate.emailSequenceTemplateMessages
                      )
                          ? // @ts-expect-error
                            emailSequenceTemplate.emailSequenceTemplateMessages.length
                          : 1,
                  }
                : undefined,
            emailSequence: emailSequence
                ? {
                      id: emailSequence.id,
                      firstMessageId: emailSequence.emailsQueued.length
                          ? // @ts-expect-error
                            emailSequence.emailsQueued[0].id
                          : null,
                      htmlUrl: emailSequence.emailsQueued.length
                          ? // @ts-expect-error
                            emailSequence.emailsQueued[0].htmlUrl
                          : '',
                      nextUnsentEmailId: emailSequence.nextUnsentEmail
                          ? emailSequence.nextUnsentEmail.id
                          : null,
                      emailSequenceTemplate: emailSequence.emailSequenceTemplate
                          ? {
                                id: emailSequence.emailSequenceTemplate.id,
                                creatorUser: {
                                    id: emailSequence.emailSequenceTemplate.creatorUser.id,
                                },
                            }
                          : null,
                  }
                : null,
            sendTime:
                emailSequence && emailSequence.emailsQueued.length
                    ? // @ts-expect-error
                      emailSequence.emailsQueued[0].deliveryTime
                    : undefined,
        };
    });
}
